import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import { IoMdArrowBack } from "react-icons/io";
import { IoCloseOutline } from "react-icons/io5";
import { FiDownload, FiEye } from "react-icons/fi";
import { MdAttachFile } from "react-icons/md";
import { hrRecordsApi } from "../../Services/hrRecordsApi";
import useClickOutside from "../../useClickOutside";
import {
  downloadBlobResponse,
  formatDateOnlyIST,
  formatDateTimeIST,
  formatFileSize,
  getApiErrorMessage,
  getApiErrorMessageAsync,
  getDuePillStyle,
  openBlobResponse,
} from "../hrRecordsHelpers";
import "../../Views/ApprovedPrView.css";
import "../../Views/voyageView.css";
import "../hrRecords.css";

function ParticipantAttachmentCell({ participant, onFileAction, onError }) {
  const [isOpen, setIsOpen] = useState(false);
  const [attachments, setAttachments] = useState(null);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  useClickOutside([containerRef], () => setIsOpen(false));

  const loadAttachments = async () => {
    if (attachments !== null || loading) {
      return;
    }

    try {
      setLoading(true);
      const response = await hrRecordsApi.listParticipantAttachments(
        participant.participant_id,
      );
      setAttachments(response.attachments || []);
    } catch (error) {
      console.error("Failed to load participant attachments:", error);
      onError(getApiErrorMessage(error, "Failed to load participant attachments"));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    setIsOpen((prev) => {
      if (!prev) {
        loadAttachments();
      }
      return !prev;
    });
  };

  return (
    <div className="hr-records-table-attachment-cell" ref={containerRef}>
      <button
        type="button"
        className="hr-records-attachment-icon-button"
        onClick={handleToggle}
        disabled={loading}
        aria-label="View participant attachments"
      >
        <MdAttachFile className="file-icon" />
      </button>

      {isOpen && (
        <div className="hr-records-table-attachment-popup">
          <div className="hr-records-table-attachment-popup-header">
            <div>Attachments</div>
            <button
              type="button"
              className="hr-records-table-attachment-popup-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close attachments"
            >
              <IoCloseOutline />
            </button>
          </div>
          <div className="hr-records-table-attachment-popup-body">
            {loading ? (
              <div className="hr-records-upload-empty">Loading...</div>
            ) : attachments?.length ? (
              attachments.map((file) => (
                <div
                  className="hr-records-table-attachment-item"
                  key={file.file_id}
                >
                  <div className="hr-records-upload-file-name">
                    {file.filename || "Attachment"}
                  </div>
                  <div className="hr-records-upload-file-meta">
                    File Size: {formatFileSize(file.file_size) || "NA"}
                  </div>
                  <div className="hr-records-table-attachment-actions">
                    <button
                      type="button"
                      onClick={() =>
                        onFileAction(
                          hrRecordsApi.downloadParticipantAttachment(file.file_id),
                          file.filename || "attachment",
                        )
                      }
                    >
                      Download
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        onFileAction(
                          hrRecordsApi.viewParticipantAttachment(file.file_id),
                          file.filename || "attachment",
                          "view",
                        )
                      }
                    >
                      View
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="hr-records-upload-empty">
                No participant files found.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TrainingSessionViewPage({ isSidebarExpanded }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const response = await hrRecordsApi.getTrainingSession(id);
      setSession(response);
    } catch (error) {
      console.error("Failed to fetch training session:", error);
      showSnackbar(
        getApiErrorMessage(error, "Failed to load training session"),
        "error",
      );
    }
  }, [id, showSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileAction = async (request, filename, action = "download") => {
    try {
      const response = await request;
      if (action === "view") {
        openBlobResponse(response);
      } else {
        downloadBlobResponse(response, filename);
      }
    } catch (error) {
      console.error("File action failed:", error);
      showSnackbar(
        await getApiErrorMessageAsync(error, "File action failed"),
        "error",
      );
    }
  };

  const groupedAttachments = session?.attachments || {};

  return (
    <>
      <div
        className={`invoice-request-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="invoice-request-header-content">
          <div className="invoice-request-heading-content-left-side">
            <Tooltip title="Back" arrow>
              <IoMdArrowBack
                className="back-company"
                onClick={() => navigate("/training/training-sessions")}
              />
            </Tooltip>
            Training Session Details
          </div>
        </div>

        <div className="data-form-body-voyage">
          {!session ? (
            <div className="loader-container">
              <div className="loader"></div>
              <div>Loading...</div>
            </div>
          ) : (
            <>
              <div className="project-detailss">
                <div className="project-detail-arround">
                  <div className="project-details-left-side">
                    {[
                      ["Session Code", session.session_code],
                      ["Training", session.training_name],
                      ["Category", session.training_category],
                      ["Standard Body", session.standard_body],
                      ["Reference No", session.standard_ref_no],
                      ["Section No", session.standard_section_no],
                      ["Start Date", formatDateOnlyIST(session.start_date)],
                      ["End Date", formatDateOnlyIST(session.end_date)],
                      ["Session Status", session.session_status],
                      ["Training Mode", session.training_mode],
                      ["Trainer Name", session.trainer_name],
                      ["Trainer Organization", session.trainer_organization],
                      ["Venue", session.venue],
                      ["Duration Hours", session.duration_hours],
                      ["Repeat Policy", `${session.effective_repeat_every || "NA"} ${session.effective_repeat_unit || ""}`],
                      ["Reminder Days", session.effective_reminder_days],
                      ["Project", session.project_name],
                      ["Created At", formatDateTimeIST(session.created_at)],
                    ].map(([label, value]) => (
                      <div className="project-detail" key={label}>
                        <div className="detail-header">{label}:</div>
                        <div className="detail-part-voyage">{value || "N/A"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="hr-records-section">
                <div className="hr-records-section-title">Session Attachments</div>
                {Object.entries(groupedAttachments).some(([, files]) => files?.length) ? (
                  Object.entries(groupedAttachments).map(([docType, files]) =>
                    files?.length ? (
                      <div key={docType} style={{ marginBottom: "18px" }}>
                        <strong style={{ textTransform: "capitalize" }}>
                          {docType.replaceAll("_", " ")}
                        </strong>
                        <div className="hr-records-file-list">
                          {files.map((file) => (
                            <div className="hr-records-file-card" key={file.file_id}>
                              <div>
                                <div className="hr-records-file-title">{file.filename}</div>
                                <div className="hr-records-file-meta">
                                  {formatFileSize(file.file_size)}
                                </div>
                              </div>
                              <div className="hr-records-inline-actions">
                                <button
                                  className="file-action-btn"
                                  onClick={() =>
                                    handleFileAction(
                                      hrRecordsApi.viewTrainingSessionAttachment(
                                        file.file_id,
                                      ),
                                      file.filename,
                                      "view",
                                    )
                                  }
                                >
                                  <FiEye /> View
                                </button>
                                <button
                                  className="file-action-btn"
                                  onClick={() =>
                                    handleFileAction(
                                      hrRecordsApi.downloadTrainingSessionAttachment(
                                        file.file_id,
                                      ),
                                      file.filename,
                                    )
                                  }
                                >
                                  <FiDownload /> Download
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null,
                  )
                ) : (
                  <div className="hr-records-empty">No session attachments found.</div>
                )}
              </div>

              <div className="hr-records-section">
                <div className="hr-records-section-title">Participants</div>
                {session.participants?.length ? (
                  <div className="data-list-table">
                    <table className="table multilevel-data">
                      <thead>
                        <tr>
                          <th>EMPLOYEE</th>
                          <th>EC NO</th>
                          <th>ATTENDANCE</th>
                          <th>RESULT</th>
                          <th>CERTIFICATE</th>
                          <th>ISSUE DATE</th>
                          <th>NEXT DUE</th>
                          <th>STATUS</th>
                          <th>ATTACHMENT</th>
                        </tr>
                      </thead>
                      <tbody className="data-list-scroll-bodys">
                        {session.participants.map((participant) => (
                          <tr
                            className="hoverable-row"
                            key={participant.participant_id}
                          >
                            <td>
                              <button
                                type="button"
                                className="button-bg"
                                onClick={() =>
                                  navigate(
                                    `/hr-training-employee-history/${participant.actual_man_power_id}`,
                                  )
                                }
                              >
                                {participant.person_name || "NA"}
                              </button>
                            </td>
                            <td>{participant.ec_no || "NA"}</td>
                            <td>{participant.attendance_status || "NA"}</td>
                            <td>{participant.result_status || "NA"}</td>
                            <td>{participant.certificate_number || "NA"}</td>
                            <td>{formatDateOnlyIST(participant.issue_date)}</td>
                            <td>{formatDateOnlyIST(participant.next_due_date)}</td>
                            <td className="center-alignment">
                              <span
                                className="hr-records-pill"
                                style={getDuePillStyle(participant.status)}
                              >
                                {participant.status || "NA"}
                              </span>
                            </td>
                            <td className="center-alignment">
                              <ParticipantAttachmentCell
                                participant={participant}
                                onFileAction={handleFileAction}
                                onError={(message) => showSnackbar(message, "error")}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="hr-records-empty">No participants found.</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ transform: "translateY(40px)", marginLeft: "15px" }}
      >
        <MuiAlert severity={snackbarSeverity}>{snackbarMessage}</MuiAlert>
      </Snackbar>
    </>
  );
}

export default TrainingSessionViewPage;
